import { useSelector } from "react-redux";
import styled from "styled-components";

import {
  CharacterBaseStats,
  CharacterDerivedStats,
  StatRanges,
  UpgradeComparisonData,
} from "types";

import { media } from "components/styles/breakpoints";
import {
  DERIVED_STATS_GROUPS,
  DerivedStatsGroupModel,
} from "data/derivedStats";
import {
  getDerivedStatRanges,
  getDerivedStats,
} from "redux/selectors/characterSelectors";
import {
  CategoryGroup,
  CategoryName,
  CategorySeparator,
} from "./DataScreenBase";
import { DerivedStat } from "./DerivedStat";
import { StatRange } from "./DerivedStatRange";

interface DerivedStatsProps {
  highlightStat: keyof CharacterBaseStats | null;
  previewDiff: UpgradeComparisonData | null;
}

const DerivedStatsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${media.desktop`
    gap: 10px;
  `}
`;

const StatList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  ${media.desktop`
    gap: 5px;
  `}
`;

export default function DerivedStats({
  highlightStat,
  previewDiff,
}: DerivedStatsProps) {
  const derivedStats = useSelector(getDerivedStats);
  const derivedStatRanges = useSelector(getDerivedStatRanges);

  return (
    <DerivedStatsWrapper>
      {Object.values(DERIVED_STATS_GROUPS).map((value) => {
        const statGroupData = value as DerivedStatsGroupModel;
        return (
          <CategoryGroup key={statGroupData.name}>
            <CategoryName>
              {statGroupData.name}
              <CategorySeparator />
            </CategoryName>
            <StatList>
              {statGroupData.stats.map((derivedStat) => {
                if (derivedStat.isRange) {
                  const statSlug = derivedStat.slug as keyof StatRanges;
                  return (
                    <StatRange
                      key={statSlug}
                      slug={statSlug}
                      statRange={derivedStatRanges.current.complete[statSlug]}
                      baseRange={derivedStatRanges.current.base[statSlug]}
                      upgradesRange={
                        derivedStatRanges.current.upgrades[statSlug]
                      }
                      highlightStat={highlightStat}
                      previewDiff={previewDiff}
                    />
                  );
                } else {
                  const statSlug =
                    derivedStat.slug as keyof CharacterDerivedStats;
                  return (
                    <DerivedStat
                      key={statSlug}
                      slug={statSlug}
                      value={derivedStats.current.complete[statSlug]}
                      baseValue={derivedStats.current.base[statSlug]}
                      upgradeValue={derivedStats.current.upgrades[statSlug]}
                      highlightStat={highlightStat}
                      previewDiff={previewDiff}
                    />
                  );
                }
              })}
            </StatList>
          </CategoryGroup>
        );
      })}
    </DerivedStatsWrapper>
  );
}
